import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Copy } from "lucide-react";
import { copyTv } from "@/pages/VaCross";
import { api, inr } from "@/lib/api";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";
import { useFlow, FlowTags } from "@/components/FlowMarks";

const SHORT = { "TFR Prime": "PRIME", "TFR Gold": "GOLD", "Power Scan": "POWER" };

export const usePlatinum = () => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/today/platinum").then((r) => setD(r.data)).catch(() => setD({ up: [], down: [] }));
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  return d;
};

const Row = ({ r, m, hot }) => (
  <div className="flex items-start gap-1">
    <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>
      <button data-testid={`plat-row-${r.side}-${r.symbol}`}
        className={`flex-1 min-w-0 rounded-lg px-2.5 py-1.5 text-left border transition-colors hover:bg-slate-100 ${r.count === 3 ? "border-slate-300 bg-slate-50" : "border-transparent"}`}>
        <div className="flex flex-wrap items-center justify-between gap-x-2">
          <span className="font-display font-bold text-slate-900 text-[13px]">{r.symbol}</span>
          <span className="flex items-center gap-2 font-mono text-[12px]">
            <span className="text-slate-900">₹{inr(r.ltp)}</span>
            <span className={(r.change_pct || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}>{r.change_pct > 0 ? "+" : ""}{r.change_pct ?? "—"}%</span>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1 mt-1">
          <span className="text-[9px] font-mono text-slate-400">{r.sector}</span>
          {Object.entries(r.scanners).map(([s, at]) => (
            <span key={s} data-testid={`plat-tag-${r.symbol}-${SHORT[s]}`} title={`${s}${at ? ` · in at ${at}` : ""}`}
              className="text-[8px] font-mono font-bold px-1 py-px rounded bg-slate-800 text-slate-100">{SHORT[s]}{at ? ` ${at}` : ""}</span>
          ))}
          {r.count === 3 && <span className="text-[8px] font-mono font-bold px-1 py-px rounded bg-gradient-to-r from-slate-300 to-slate-100 text-slate-900 border border-slate-400" data-testid={`plat-all3-${r.symbol}`}>ALL 3</span>}
          {hot?.top?.includes(r.sector) && <span className="text-[8px] font-mono font-bold px-1 py-px rounded bg-emerald-600 text-white">TOP</span>}
          {hot?.bottom?.includes(r.sector) && <span className="text-[8px] font-mono font-bold px-1 py-px rounded bg-rose-600 text-white">WORST</span>}
          <FlowTags m={m} testid={`plat-${r.symbol}`} />
        </div>
      </button>
    </StockMenu>
    <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} className="mt-2" />
  </div>
);

export function PlatinumPanel({ limit }) {
  const d = usePlatinum();
  const flow = useFlow();
  const [mode, setMode] = useState(localStorage.getItem("tfr_plat_mode") || "open");
  const pick = (v) => { localStorage.setItem("tfr_plat_mode", v); setMode(v); };
  if (!d) return <p className="text-[11px] font-mono text-slate-400 py-3 text-center">Loading…</p>;
  const field = { open: "open_x", big: "bm_x" }[mode];
  const keep = (rows = []) => (field ? rows.filter((r) => (flow.marks[r.symbol]?.[field] || 0) >= flow.min) : rows);
  return (
    <div data-testid="platinum-panel">
      <div className="flex flex-wrap items-center gap-1 mb-2" data-testid="plat-filter">
        {[["open", "Opening 3×+"], ["big", "Big Money 3×+"], ["all", "All common stocks"]].map(([v, lbl]) => (
          <button key={v} onClick={() => pick(v)} data-testid={`plat-filter-${v}`}
            className={`px-2 h-6 rounded text-[10px] font-mono font-bold border transition-colors ${mode === v ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-500 hover:text-slate-900"}`}>{lbl}</button>
        ))}
        {!limit && (
          <button onClick={() => copyTv([...keep(d.up), ...keep(d.down)])} data-testid="plat-copy-tv"
            className="ml-auto h-6 px-2.5 rounded text-[10px] font-mono border border-slate-300 text-slate-800 hover:border-blue-500 transition-colors flex items-center gap-1.5">
            <Copy className="h-3 w-3 text-blue-600" /> Copy to TradingView
          </button>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {[["up", "Bullish", TrendingUp, "text-emerald-600"], ["down", "Bearish", TrendingDown, "text-rose-600"]].map(([side, lbl, Ic, cls]) => {
          const rows = keep(d[side]);
          return (
            <div key={side} data-testid={`plat-list-${side}`}>
              <div className={`flex items-center gap-1 text-[10px] font-semibold mb-1.5 ${cls}`}><Ic className="h-3 w-3" /> {lbl} <span className="text-slate-400 font-mono" data-testid={`plat-count-${side}`}>({rows.length})</span></div>
              <div className="space-y-1">
                {(limit ? rows.slice(0, limit) : rows).map((r) => <Row key={r.symbol} r={r} m={flow.marks[r.symbol]} hot={d.hot} />)}
              </div>
              {!rows.length && <p className="text-slate-400 text-[11px] py-3 text-center font-mono" data-testid={`plat-empty-${side}`}>none right now</p>}
              {limit && rows.length > limit && <p className="text-[10px] font-mono text-slate-400 text-center pt-1">+{rows.length - limit} more on the Platinum page</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
